import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, Chip, CircularProgress, FormControl, ListItemText, MenuItem,
  Paper, Select, TextField, Typography,
} from '@mui/material';
import { IoDownloadOutline, IoRefreshOutline } from 'react-icons/io5';
import * as XLSX from 'xlsx-js-style';
import AppDatatable from '../../components/AppDatatable';
import { exportClaimsPdf } from '../../utils/exportClaimsPdf';
import { getLoggedInUser } from '../../utils/permissions';
import { getDateRangeParams, getDefaultWeekRange } from '../../utils/dateRange';

const ENDPOINTS = [
  { url: '/get_closed_forms', workflow: 'Vehicle Damage', title: 'Vehicle Damage Claims' },
  { url: '/get_closed_windshield_tire_forms', workflow: 'Windshield / Tire', title: 'Windshield/Tire Claims' },
  { url: '/get_closed_shuttle_bus_forms', workflow: 'Shuttle Bus', title: 'Shuttle Bus Claims' },
];

const HEADERS = [
  'R/A Number', 'Unit', 'Year & Make', 'Model & Colour', 'VIN',
  'Repaired Amount', 'Charged/Received Amount', 'LOU', 'ADMIN FEE',
  'Tow Check', 'Salvage Amount', 'Sold', 'Amount due from customer', 'ACV',
  'Chargeback (Yes/No)', 'Difference', 'Charged Customer', 'Check Received',
  'Stripe/Wire', 'Internal notes', 'Repaired AT',
];

const firstValue = (...values) => values.find((value) => value !== undefined && value !== null && String(value).trim() !== '') ?? '';
const normalizeLocations = (value) => (Array.isArray(value) ? value : String(value || '').split(','))
  .map((item) => String(item).trim().toLowerCase())
  .filter(Boolean);
const firstVehicle = (row) => Array.isArray(row?.details_by_vehicle) ? (row.details_by_vehicle[0] || {}) : {};
const isActuallyClosed = (row) => Boolean(
  row?.closure_completed
  || row?.claim_status === 'Completed'
  || row?.claim_workflow_stage === 'closed'
);
const asDate = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};
const displayDate = (value) => asDate(value)?.toLocaleDateString('en-CA') || '';
const displayMoney = (value) => {
  if (value === '' || value === null || value === undefined) return '-';
  const number = Number(value);
  return Number.isFinite(number)
    ? new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(number)
    : String(value);
};
const notesFor = (row) => {
  const entries = Array.isArray(row.internal_note_entries) ? row.internal_note_entries : [];
  return firstValue(entries.map((entry) => entry?.notes).filter(Boolean).join(' | '), row.internal_notes);
};

const toRow = (row, workflow) => {
  const repaired = Number(row.repaired_amount);
  const received = Number(row.charged_received_amount);
  const difference = Number((
    (Number.isFinite(received) ? received : 0) - (Number.isFinite(repaired) ? repaired : 0)
  ).toFixed(2));
  const reported = row.reported_claim || {};
  const vehicle = firstVehicle(row);
  const reportedVehicle = firstVehicle(reported);
  const claimVehicle = Object.keys(vehicle).length ? vehicle : reportedVehicle;
  const make = firstValue(row.year_make, [claimVehicle.year, claimVehicle.make].filter(Boolean).join(' '));
  const model = firstValue(row.model_colour, [claimVehicle.model, claimVehicle.color].filter(Boolean).join('*'));
  const result = {
    id: `${workflow}-${row._id}`,
    workflow,
    location: firstValue(row.branchLocationCode, row.claimOwningBranch, row.location_name, reported.branchLocationCode, reported.claimOwningBranch, 'Unassigned'),
    raNumber: firstValue(row.ra_number, row.rentalAgreementNumber, reported.rentalAgreementNumber),
    unit: firstValue(row.unit, claimVehicle.unitFleetNumber),
    modelColour: model,
    yearMake: make,
    vin: firstValue(row.vin, claimVehicle.vin),
    repairedAmount: firstValue(row.repaired_amount),
    receivedAmount: firstValue(row.charged_received_amount),
    lou: firstValue(row.lou),
    adminFee: firstValue(row.admin_fee),
    towCheck: firstValue(row.tow_check),
    salvageAmount: firstValue(row.salvage_amount),
    sold: firstValue(row.sold),
    storageCharge: firstValue(row.storage_charge),
    amountDueCustomer: firstValue(row.amount_due_customer),
    currentCost: firstValue(row.current_cost),
    acv: firstValue(row.acv),
    chargeback: firstValue(row.chargeback),
    difference,
    chargedCustomer: firstValue(row.charged_customer),
    checkReceived: firstValue(row.check_received),
    cash: firstValue(row.cash),
    wireTransfer: firstValue(row.wire_transfer),
    internalNotes: notesFor(row),
    repairedAt: firstValue(row.repaired_at),
    closedDate: firstValue(row.closed_datetime, row.closure_datetime, row.updated_datetime, row.created_datetime),
    dateOfLoss: firstValue(row.accidentDate, row.date_of_loss, reported.accidentDate, reported.date_of_loss),
    plateNumber: firstValue(row.plate_number, row.licensePlate, claimVehicle.licensePlate),
    mileage: firstValue(row.mileage, row.odometerReading, claimVehicle.odometerReading),
    insurance: firstValue(
      row.customerInsuranceCarrier, row.insurance_route, row.insuranceType,
      reported.customerInsuranceCarrier, reported.insurance_route, reported.insuranceType
    ),
    tpInsuranceNumber: firstValue(
      row.tp_insurance_number,
      [
        firstValue(row.liability_insurance_name, reported.liability_insurance_name),
        firstValue(row.insurance_claim_number, reported.insurance_claim_number),
      ].filter(Boolean).join(' - ')
    ),
    preAuth: firstValue(row.pre_auth, row.preAuthorizationAmount, reported.preAuthorizationAmount),
    preAuthTaken: firstValue(row.pre_auth_taken, row.preAuthorizationTaken, reported.preAuthorizationTaken),
    damage: firstValue(row.damage, claimVehicle.pointOfImpactDetails, claimVehicle.pointOfImpact),
    fault: firstValue(row.party_at_fault, row.atFaultNotAtFault, reported.party_at_fault, reported.atFaultNotAtFault),
    accidentNarrative: firstValue(row.accident_narrative, row.accidentDescription, reported.accidentDescription),
    claimEntryDate: firstValue(row.claim_entry_date, row.created_datetime, reported.created_datetime),
    renterDriverName: firstValue(
      row.authorizedDriverName, row.driverAtTimeOfAccident,
      reported.authorizedDriverName, reported.driverAtTimeOfAccident
    ),
    demand: firstValue(row.original_demand_amount, row.demand, row.demand_balance),
    proAppraisal: firstValue(row.pro_appraisal, row.proAppraisal),
    tt: firstValue(row.t_and_t, row.tt, row['t&t']),
    aspen: firstValue(row.aspen),
    proFleet: firstValue(row.pro_fleet, row.proFleet),
    familyAuto: firstValue(row.family_auto, row.familyAuto),
    approvedEstimate: firstValue(row.approved_estimate, row.amount_approved),
    handlingSupervisor: firstValue(row.handling_supervisor, row.handlingSupervisor, row.updated_by, row.closed_by),
    claimStatus: firstValue(row.claim_status, row.claim_workflow_stage),
  };
  return result;
};

const EXCEL_KEYS = [
  'raNumber', 'unit', 'yearMake', 'modelColour', 'vin', 'repairedAmount',
  'receivedAmount', 'lou', 'adminFee', 'towCheck', 'salvageAmount', 'sold',
  'amountDueCustomer', 'acv', 'chargeback', 'difference', 'chargedCustomer',
  'checkReceived', 'wireTransfer', 'internalNotes', 'repairedAt',
];
const EXCEL_MONEY_KEYS = new Set([
  'repairedAmount', 'receivedAmount', 'lou', 'adminFee', 'towCheck',
  'salvageAmount', 'sold', 'amountDueCustomer', 'acv', 'difference',
  'chargedCustomer', 'checkReceived', 'wireTransfer',
]);
const EXCEL_DATE_KEYS = new Set(['dateOfLoss', 'claimEntryDate', 'closedDate']);
const EXCEL_TOTAL_KEYS = [
  'repairedAmount', 'receivedAmount', 'lou', 'adminFee', 'amountDueCustomer',
  'difference', 'chargedCustomer', 'checkReceived', 'wireTransfer',
];
const excelColumnTotal = (rows, key) => Number(rows.reduce((sum, row) => {
  const value = Number(row[key]);
  return sum + (Number.isFinite(value) ? value : 0);
}, 0).toFixed(2));
const EXCEL_EMPTY_DEFAULTS = {
  lou: 0,
  adminFee: 0,
  towCheck: 'No',
  salvageAmount: 'N/A',
  sold: 'No',
  amountDueCustomer: 0,
  acv: 'N/A',
  chargeback: 'N/A',
  chargedCustomer: 0,
  checkReceived: 0,
  wireTransfer: 0,
};
const withExcelDefaults = (row) => {
  const result = { ...row };
  Object.entries(EXCEL_EMPTY_DEFAULTS).forEach(([key, fallback]) => {
    if (result[key] === undefined || result[key] === null || String(result[key]).trim() === '') {
      result[key] = fallback;
    }
  });
  return result;
};
const excelRow = (row) => EXCEL_KEYS.map((key) => {
  const value = row[key];
  if (EXCEL_DATE_KEYS.has(key)) return asDate(value) || value;
  if (EXCEL_MONEY_KEYS.has(key) && value !== '') {
    const number = Number(value);
    return Number.isFinite(number) ? number : value;
  }
  return value;
});
const safeSheetName = (name, used) => {
  const base = String(name || 'Unassigned').replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31) || 'Unassigned';
  let candidate = base;
  let index = 2;
  while (used.has(candidate)) candidate = `${base.slice(0, 27)} ${index++}`.slice(0, 31);
  used.add(candidate);
  return candidate;
};
const formatExcelSheet = (sheet, values) => {
  values.forEach((valuesRow, row) => {
    if (!valuesRow.length) return;
    HEADERS.forEach((_, col) => {
      const address = XLSX.utils.encode_cell({ r: row, c: col });
      const cell = sheet[address] ||= { t: 's', v: '' };
      cell.s = {
        ...cell.s,
        font: { ...cell.s?.font, name: 'Roboto', sz: 9 },
        alignment: { ...cell.s?.alignment, horizontal: 'center', vertical: 'center', wrapText: false },
        border: Object.fromEntries(['top', 'bottom', 'left', 'right'].map((side) => [
          side, { style: 'thin', color: { rgb: '000000' } },
        ])),
      };
    });
  });
};
const addExcelSummary = (sheet, rows, startRow) => {
  const summary = [
    { label: 'Received Amount', key: 'receivedAmount', color: '00B0F0' },
    { label: 'Repair Amount', key: 'repairedAmount', color: 'FF9900' },
    { label: 'Difference Amount', key: 'difference', color: '00B050' },
  ];
  summary.forEach(({ label, key, color }, index) => {
    const row = startRow + index;
    XLSX.utils.sheet_add_aoa(sheet, [[excelColumnTotal(rows, key), '', '']], { origin: { r: row, c: 3 } });
    (sheet['!merges'] ||= []).push({ s: { r: row, c: 3 }, e: { r: row, c: 5 } });
    for (let col = 3; col <= 5; col += 1) {
      const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
      cell.s = {
        font: { name: 'Roboto', sz: 9, bold: true, color: { rgb: '000000' } },
        fill: { patternType: 'solid', fgColor: { rgb: color } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          ...(col === 3 ? { left: { style: 'thin', color: { rgb: '000000' } } } : {}),
          ...(col === 5 ? { right: { style: 'thin', color: { rgb: '000000' } } } : {}),
        },
      };
      if (col === 3) cell.z = `"${label}: "$#,##0.00;"${label}: "-$#,##0.00`;
    }
    (sheet['!rows'] ||= [])[row] = { hpt: 21 };
  });
};

export default function ClosedClaimsReport() {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const currentUser = useMemo(() => getLoggedInUser(), []);
  const assignedLocations = useMemo(
    () => normalizeLocations(currentUser?.selected_locations || currentUser?.locations),
    [currentUser]
  );
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [error, setError] = useState('');
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [workflow, setWorkflow] = useState('All');
  const [fromDate, setFromDate] = useState(() => getDefaultWeekRange().fromDate);
  const [toDate, setToDate] = useState(() => getDefaultWeekRange().toDate);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const responses = await Promise.all(ENDPOINTS.map((endpoint) => axios.get(`${baseURL}${endpoint.url}`, { params: getDateRangeParams(fromDate, toDate) })));
      setRows(responses.flatMap((response, index) => {
        const payload = response.data;
        const records = Array.isArray(payload) ? payload : (payload?.data || payload?.records || []);
        return records
          .filter(isActuallyClosed)
          .map((record) => toRow(record, ENDPOINTS[index].workflow));
      }));
    } catch (requestError) {
      setError(requestError?.response?.data?.message || 'Closed claims could not be loaded.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [baseURL, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const scopedRows = useMemo(() => {
    if (!assignedLocations.length) return [];
    return rows.filter((row) => assignedLocations.includes(String(row.location || '').trim().toLowerCase()));
  }, [assignedLocations, rows]);
  const locations = useMemo(() => [...new Set(scopedRows.map((row) => row.location))].sort(), [scopedRows]);
  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const from = fromDate ? new Date(`${fromDate}T00:00:00`) : null;
    const to = toDate ? new Date(`${toDate}T23:59:59.999`) : null;
    return scopedRows.filter((row) => {
      const closed = asDate(row.closedDate);
      return (!selectedLocations.length || selectedLocations.includes(row.location))
        && (workflow === 'All' || row.workflow === workflow)
        && (!from || (closed && closed >= from))
        && (!to || (closed && closed <= to))
        && (!query || Object.values(row).some((value) => String(value ?? '').toLowerCase().includes(query)));
    });
  }, [scopedRows, selectedLocations, workflow, fromDate, toDate, search]);

  const exportPdf = async () => {
    setExportingPdf(true);
    setError('');
    try {
      await exportClaimsPdf({
        title: 'Closed Claims Report', headers: HEADERS, keys: EXCEL_KEYS,
        moneyKeys: EXCEL_MONEY_KEYS, rows: filteredRows, sections: ENDPOINTS,
        valuesFor: excelRow,
        filename: `Closed_Claims_Report_${new Date().toISOString().slice(0, 10)}.pdf`,
      });
    } catch (exportError) {
      setError('PDF export failed. Please try again.');
    } finally {
      setExportingPdf(false);
    }
  };

  const exportReport = () => {
    const workbook = XLSX.utils.book_new();
    const headers = HEADERS;
    const grouped = filteredRows.reduce((groups, row) => {
      (groups[row.location] ||= []).push(row);
      return groups;
    }, {});
    const usedNames = new Set();
    Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b)).forEach(([branch, branchRows]) => {
      const values = [];
      const sectionRows = [];
      const headerRows = [];
      const totalRows = [];
      ENDPOINTS.forEach((section, index) => {
        sectionRows.push(values.length);
        values.push([section.title]);
        headerRows.push(values.length);
        values.push(headers);
        const records = branchRows
          .filter((row) => row.workflow === section.workflow)
          .sort((a, b) => {
            const first = asDate(a.closedDate)?.getTime() ?? Infinity;
            const second = asDate(b.closedDate)?.getTime() ?? Infinity;
            return first === second ? 0 : first - second;
          });
        values.push(...(records.length ? records.map((row) => excelRow(withExcelDefaults(row))) : [['No closed claims for this section']]));
        if (records.length) {
          totalRows.push(values.length);
          values.push(EXCEL_KEYS.map((key) => (
            EXCEL_TOTAL_KEYS.includes(key) ? excelColumnTotal(records, key) : ''
          )));
        }
        if (index < ENDPOINTS.length - 1) values.push([]);
      });
      const sheet = XLSX.utils.aoa_to_sheet(values);
      sheet['!merges'] = sectionRows.map((row) => ({ s: { r: row, c: 0 }, e: { r: row, c: headers.length - 1 } }));
      sectionRows.forEach((row) => {
        for (let col = 0; col < headers.length; col += 1) {
          const address = XLSX.utils.encode_cell({ r: row, c: col });
          if (!sheet[address]) sheet[address] = { t: 's', v: '' };
          sheet[address].s = {
            font: { bold: true, color: { rgb: 'FFFFFF' } },
            fill: { patternType: 'solid', fgColor: { rgb: '173B70' } },
            alignment: { vertical: 'center' },
          };
        }
      });
      headerRows.forEach((row) => {
        for (let col = 0; col < headers.length; col += 1) {
          const cell = sheet[XLSX.utils.encode_cell({ r: row, c: col })];
          if (cell) cell.s = {
            font: { bold: true, color: { rgb: '173B70' } },
            fill: { patternType: 'solid', fgColor: { rgb: 'DCE8F7' } },
            alignment: { vertical: 'center', wrapText: false },
          };
        }
      });
      sheet['!rows'] = values.map((_, rowIndex) => ({
        hpt: sectionRows.includes(rowIndex) ? 24 : 18,
      }));
      sheet['!cols'] = headers.map((_, index) => ({
        wch: [16, 14, 20, 22, 22, 20, 26, 14, 16, 16, 20, 16, 28, 16,
          22, 18, 22, 20, 20, 40, 24][index],
      }));
      Object.keys(sheet).forEach((address) => {
        if (address.startsWith('!')) return;
        const cell = sheet[address];
        cell.s = { ...cell.s, alignment: { ...cell.s?.alignment, wrapText: false } };
        const { r: row, c: col } = XLSX.utils.decode_cell(address);
        const key = EXCEL_KEYS[col];
        if (totalRows.includes(row) && EXCEL_TOTAL_KEYS.includes(key)) {
          cell.s.font = { ...cell.s.font, bold: true };
        }
        if (EXCEL_MONEY_KEYS.has(key) && sheet[address]?.t === 'n') sheet[address].z = '$#,##0.00';
        if (EXCEL_DATE_KEYS.has(key) && sheet[address]?.v instanceof Date) sheet[address].z = 'yyyy-mm-dd';
      });
      formatExcelSheet(sheet, values);
      sectionRows.forEach((row) => {
        const cell = sheet[XLSX.utils.encode_cell({ r: row, c: 0 })];
        cell.s.alignment.horizontal = 'left';
      });
      addExcelSummary(sheet, branchRows, values.length + 4);
      XLSX.utils.book_append_sheet(workbook, sheet, safeSheetName(branch, usedNames));
    });
    if (!filteredRows.length) {
      const sheet = XLSX.utils.aoa_to_sheet([headers]);
      formatExcelSheet(sheet, [headers]);
      addExcelSummary(sheet, [], 5);
      XLSX.utils.book_append_sheet(workbook, sheet, 'Closed Claims');
    }
    XLSX.writeFile(workbook, `Closed_Claims_Report_${new Date().toISOString().slice(0, 10)}.xlsx`, { cellStyles: true });
  };

  const tableRows = filteredRows.map((row) => ({
    ...row,
    closedDateDisplay: displayDate(row.closedDate),
    repairedAmountDisplay: displayMoney(row.repairedAmount),
    receivedAmountDisplay: displayMoney(row.receivedAmount),
    differenceDisplay: displayMoney(row.difference),
  }));
  const columns = [
    { field: 'location', label: 'Location', sortable: true },
    { field: 'workflow', label: 'Claim Type', sortable: true },
    { field: 'raNumber', label: 'R/A Number', sortable: true },
    { field: 'unit', label: 'Unit', sortable: true },
    { field: 'closedDateDisplay', label: 'Closed Date', sortable: true, getSortValue: (row) => row.closedDate },
    { field: 'repairedAmountDisplay', label: 'Repaired Amount', sortable: true, getSortValue: (row) => Number(row.repairedAmount) || 0 },
    { field: 'receivedAmountDisplay', label: 'Charged/Received', sortable: true, getSortValue: (row) => Number(row.receivedAmount) || 0 },
    { field: 'differenceDisplay', label: 'Difference', sortable: true, getSortValue: (row) => Number(row.difference) || 0 },
    { field: 'repairedAt', label: 'Repaired At', sortable: true },
  ];
  const fieldLabelSx = { display: 'block', mb: 0.75, ml: 0.25, fontSize: 13, fontWeight: 600, color: '#36577f' };

  return (
    <Box sx={{ ml: { md: '240px' }, mt: '64px', p: { xs: 1.5, sm: 2.5, lg: 4 }, minHeight: 'calc(100vh - 64px)', bgcolor: '#f5f7fb', overflow: 'hidden' }}>
      <Paper sx={{ p: { xs: 2, sm: 3, lg: 4 }, borderRadius: { xs: 2, md: 4 }, boxShadow: '0 12px 35px rgba(23,57,113,.07)', overflow: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', gap: 2.5, mb: 3 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: 28, md: 36 }, lineHeight: 1.15, fontWeight: 800, color: '#123b76' }}>Closed Claims Report</Typography>
            <Typography sx={{ mt: 0.75, color: 'text.secondary', fontSize: { xs: 14, md: 16 } }}>Review closed claims and export the closed-claims workbook.</Typography>
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(4, minmax(0, 1fr))' }, alignItems: 'center', gap: 1.5, alignSelf: 'flex-end', width: { xs: '100%', sm: 'auto' }, '& > .MuiButton-root, & > .MuiChip-root': { width: '100%', height: 44, minWidth: { xs: 0, sm: 140 }, whiteSpace: 'nowrap' }, '& > .MuiButton-root': { px: { xs: 1, sm: 2 }, fontSize: { xs: 12, sm: 14 } } }}>
            <Chip sx={{ height: 44, bgcolor: '#f5f9ff' }} label={`${filteredRows.length} closed claim${filteredRows.length === 1 ? '' : 's'}`} color="primary" variant="outlined" />
            <Button sx={{ height: 44, minWidth: 118, borderRadius: 2 }} variant="outlined" startIcon={<IoRefreshOutline />} onClick={load} disabled={loading}>Refresh</Button>
            <Button sx={{ height: 44, minWidth: 154, borderRadius: 2 }} variant="contained" startIcon={<IoDownloadOutline />} onClick={exportReport} disabled={loading}>Export Excel</Button>
            <Button sx={{ height: 44, minWidth: 154, borderRadius: 2 }} variant="outlined" startIcon={<IoDownloadOutline />} onClick={exportPdf} disabled={loading || exportingPdf}>{exportingPdf ? 'Exporting PDF...' : 'Export PDF'}</Button>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', xl: '1fr 1fr 1fr 1fr' }, gap: 2, mb: 3, p: { xs: 2, md: 2.5 }, borderRadius: 2.5, bgcolor: '#f7f9fc', border: '1px solid #e7edf5' }}>
          <Box><Typography sx={fieldLabelSx}>Location</Typography><FormControl size="small" fullWidth><Select multiple value={selectedLocations} onChange={(event) => setSelectedLocations(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)} displayEmpty renderValue={(selected) => selected.length ? selected.join(', ') : 'All assigned locations'}>{locations.map((item) => <MenuItem key={item} value={item}><Checkbox checked={selectedLocations.includes(item)} /><ListItemText primary={item} /></MenuItem>)}</Select></FormControl></Box>
          <Box><Typography sx={fieldLabelSx}>Claim type</Typography><FormControl size="small" fullWidth><Select value={workflow} onChange={(event) => setWorkflow(event.target.value)}><MenuItem value="All">All claim types</MenuItem>{ENDPOINTS.map((item) => <MenuItem key={item.workflow} value={item.workflow}>{item.workflow}</MenuItem>)}</Select></FormControl></Box>
          <Box><Typography component="label" htmlFor="closed-date-from" sx={fieldLabelSx}>Closed date from</Typography><TextField id="closed-date-from" type="date" value={fromDate} onChange={(event) => setFromDate(event.target.value)} size="small" fullWidth inputProps={{ max: toDate || undefined }} /></Box>
          <Box><Typography component="label" htmlFor="closed-date-to" sx={fieldLabelSx}>Closed date to</Typography><TextField id="closed-date-to" type="date" value={toDate} onChange={(event) => setToDate(event.target.value)} size="small" fullWidth inputProps={{ min: fromDate || undefined }} /></Box>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <Box py={8} textAlign="center"><CircularProgress /></Box> : (
          <Box sx={{ '& > .MuiPaper-root': { p: 0, boxShadow: 'none' }, '& .MuiTableContainer-root': { border: '1px solid #e8edf4', borderRadius: 2 }, '& .MuiTableHead-root': { bgcolor: '#f6f8fc' }, '& .MuiTableCell-head': { color: '#173b70' } }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}><TextField label="Search table" value={search} onChange={(event) => setSearch(event.target.value)} size="small" sx={{ width: { xs: '100%', sm: 360 } }} /></Box>
            <AppDatatable data={{ rows: tableRows, columns }} hideSearch tableMinWidth={1200} noFoundMessage="No closed claims match the selected filters." />
          </Box>
        )}
      </Paper>
    </Box>
  );
}
